<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import SortableList from '@js/components/lists/SortableList.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { boolean, object, string } from 'yup';

const { prospect } = defineProps({
    prospect: {
        required: false,
        type: Object,
    },
});

const { route } = useRoute();

let contactUidCounter = 0;

function nextContactUid() {
    return `${++contactUidCounter}`;
}

const form = reactive({
    name: prospect ? prospect.name : '',
    url: prospect ? prospect.url : null,
    isArchived: prospect ? prospect.isArchived : false,
    internalNotes: prospect ? prospect.internalNotes : null,
    contactDate: prospect ? prospect.contactDate : null,
    contacts:
        prospect && prospect.contacts && prospect.contacts.length > 0
            ? prospect.contacts.map((contact) => ({
                  ...contact,
                  _uid: nextContactUid(),
              }))
            : [],
});

const isSubmitting = ref(false);

const baseValidationSchema = object({
    name: string().required('Ce champ est obligatoire.'),
    url: string().url('Veuillez saisir une URL valide.').nullable(),
    isArchived: boolean().required('Ce champ est obligatoire.'),
    internalNotes: string().nullable(),
    contactDate: string().nullable(),
});

const validationSchema = computed(() => {
    const dynamicSchema = {};

    form.contacts.forEach((contact) => {
        dynamicSchema[`contactName_${contact._uid}`] = string().required(
            'Ce champ est obligatoire.',
        );
        dynamicSchema[`contactEmail_${contact._uid}`] = string()
            .email('Veuillez saisir une adresse email valide.')
            .nullable();
    });

    return baseValidationSchema.shape(dynamicSchema);
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            prospect
                ? route('front.projectManagement.apikom.postProspect', {
                      id: prospect.id,
                  })
                : route('front.projectManagement.apikom.postProspect'),
            {
                name: form.name,
                url: form.url,
                isArchived: form.isArchived,
                internalNotes: form.internalNotes,
                contactDate: form.contactDate,
                contacts: form.contacts.map((contact) => ({
                    id: contact.id ?? null,
                    name: contact.name,
                    email: contact.email,
                    phone: contact.phone,
                })),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(route('front.projectManagement.apikom.prospects'));
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);
            router.visit(route('front.projectManagement.apikom.prospects'));
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <TabLayout
            :tabs="{
                information: {
                    title: 'Informations',
                    forceMount: true,
                    contentSlotName: 'information',
                },
                internalNotes: {
                    title: 'Notes internes',
                    forceMount: true,
                    contentSlotName: 'internalNotes',
                },
            }"
        >
            <template #information>
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="2">
                            <InputField
                                v-model="form.name"
                                label="Nom"
                                field-name="name"
                                field-id="name"
                                type="text"
                                required
                            />

                            <InputField
                                v-model="form.url"
                                label="URL"
                                field-name="url"
                                field-id="url"
                                type="url"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <InputField
                            v-model="form.contactDate"
                            label="Date de contact"
                            field-name="contactDate"
                            field-id="contactDate"
                            type="date"
                        />
                    </FormContent>

                    <FormContent>
                        <SwitchField
                            v-model="form.isArchived"
                            label="Archivé"
                            field-name="isArchived"
                            field-id="isArchived"
                        />
                    </FormContent>

                    <FormContent>
                        <SortableList
                            v-model="form.contacts"
                            :sortable="false"
                            add-label="Ajouter un contact"
                            empty-text="Aucun contact pour le moment."
                            :new-item="
                                () => ({
                                    id: null,
                                    name: '',
                                    email: '',
                                    phone: '',
                                    _uid: nextContactUid(),
                                })
                            "
                        >
                            <template #default="{ item }">
                                <FormGrid :columns="3">
                                    <InputField
                                        v-model="item.name"
                                        label="Nom"
                                        :field-name="`contactName_${item._uid}`"
                                        :field-id="`contactName_${item._uid}`"
                                        type="text"
                                        required
                                    />

                                    <InputField
                                        v-model="item.email"
                                        label="Email"
                                        :field-name="`contactEmail_${item._uid}`"
                                        :field-id="`contactEmail_${item._uid}`"
                                        type="email"
                                    />

                                    <InputField
                                        v-model="item.phone"
                                        label="Téléphone"
                                        :field-name="`contactPhone_${item._uid}`"
                                        :field-id="`contactPhone_${item._uid}`"
                                        type="tel"
                                    />
                                </FormGrid>
                            </template>
                        </SortableList>
                    </FormContent>
                </FormColumn>
            </template>

            <template #internalNotes>
                <FormColumn>
                    <FormContent>
                        <EditorField
                            v-model="form.internalNotes"
                            label="Notes internes"
                            field-name="internalNotes"
                            field-id="internalNotes"
                        />
                    </FormContent>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="prospect"
                    :url="
                        route('front.projectManagement.apikom.deleteProspect', {
                            prospect: prospect.id,
                        })
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.apikom.prospects',
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
